import { useParams } from "react-router-dom";
import { DataTable, DataTableProps } from "../../components/dataTable";
import { memo, useCallback } from "react";

interface DefaultTableProps extends DataTableProps {
  setOwnerId?: boolean;
}

const DefaultTable = memo(({ queryArgs, setOwnerId, ...rest }: DefaultTableProps) => {
  const params = useParams();

  if (setOwnerId) {
    const ownerId = params.recordId ? parseInt(params.recordId) : 0;
    if (queryArgs) {
      queryArgs.ownerId = ownerId;
    } else {
      queryArgs = { ownerId };
    }
  }

  return <DataTable queryArgs={queryArgs} {...rest} />;
});

interface DefaultTableWithDeleteMutationProps extends DataTableProps {
  deleteMutation: Function;
  multiDeleteMutation: Function;
  setOwnerId?: boolean;
}

const DefaultTableWithDeleteMutation = memo(
  ({ queryArgs, deleteMutation, multiDeleteMutation, editCallback, setOwnerId, ...rest }: DefaultTableWithDeleteMutationProps) => {
    const params = useParams();

    if (setOwnerId) {
      const ownerId = params.recordId ? parseInt(params.recordId) : 0;
      if (queryArgs) {
        queryArgs.ownerId = ownerId;
      } else {
        queryArgs = { ownerId };
      }
    }

    const [deleteTrigger] = deleteMutation();
    const [multiDeleteTrigger] = multiDeleteMutation();

    const deleteCallback = useCallback(
      async (id: number | string) => {
        try {
          await deleteTrigger(id).unwrap();
          return true;
        } catch {
          return false;
        }
      },
      [deleteTrigger]
    );

    const multiDeleteCallback = useCallback(
      async (ids: number[] | string[]) => {
        try {
          await multiDeleteTrigger({ ids }).unwrap();
          return true;
        } catch {
          return false;
        }
      },
      [multiDeleteTrigger]
    );

    return (
      <DataTable
        queryArgs={queryArgs}
        deleteCallback={deleteCallback}
        multiDeleteCallback={multiDeleteCallback}
        editCallback={editCallback}
        {...rest}
      />
    );
  }
);

interface DefaultTableWithEditAndDeleteMutationProps extends DataTableProps {
  editMutation: Function;
  deleteMutation: Function;
  multiDeleteMutation: Function;
  setOwnerId?: boolean;
}

const DefaultTableWithEditAndDeleteMutation = memo(
  ({ queryArgs, editMutation, deleteMutation, multiDeleteMutation, setOwnerId, ...rest }: DefaultTableWithEditAndDeleteMutationProps) => {
    const params = useParams();

    if (setOwnerId) {
      const ownerId = params.recordId ? parseInt(params.recordId) : 0;
      if (queryArgs) {
        queryArgs.ownerId = ownerId;
      } else {
        queryArgs = { ownerId };
      }
    }

    const [editTrigger] = editMutation();
    const [deleteTrigger] = deleteMutation();
    const [multiDeleteTrigger] = multiDeleteMutation();

    const editCallback = useCallback(
      async (record: any) => {
        try {
          await editTrigger(record).unwrap();
          return true;
        } catch {
          return false;
        }
      },
      [editTrigger]
    );

    const deleteCallback = useCallback(
      async (id: number | string) => {
        try {
          await deleteTrigger(id).unwrap();
          return true;
        } catch {
          return false;
        }
      },
      [deleteTrigger]
    );

    const multiDeleteCallback = useCallback(
      async (ids: number[] | string[]) => {
        try {
          await multiDeleteTrigger({ ids }).unwrap();
          return true;
        } catch {
          return false;
        }
      },
      [multiDeleteTrigger]
    );

    return (
      <DataTable
        queryArgs={queryArgs}
        deleteCallback={deleteCallback}
        multiDeleteCallback={multiDeleteCallback}
        editCallback={editCallback}
        {...rest}
      />
    );
  }
);

interface GetDefaultTableProps extends DataTableProps {
  deleteMutation?: Function;
  multiDeleteMutation?: Function;
  editMutation?: Function;
  setOwnerId?: boolean;
}

export const GetDefaultTable =
  ({ deleteMutation, multiDeleteMutation, editMutation, ...rest }: GetDefaultTableProps) =>
  () => {
    if (editMutation && deleteMutation && multiDeleteMutation) {
      return (
        <DefaultTableWithEditAndDeleteMutation
          editMutation={editMutation}
          deleteMutation={deleteMutation}
          multiDeleteMutation={multiDeleteMutation}
          {...rest}
        />
      );
    }

    if (deleteMutation && multiDeleteMutation) {
      return <DefaultTableWithDeleteMutation deleteMutation={deleteMutation} multiDeleteMutation={multiDeleteMutation} {...rest} />;
    }

    return <DefaultTable {...rest} />;
  };
